// @ts-check
import { getActivePinia } from 'pinia';

/** @typedef {import('pinia').Pinia & {_s: Map<string, import('pinia').Store>}} ExtendedPinia */

/**
 * @returns {Promise<Record<string, import('pinia').Store>>} The loaded stores.
 */
async function loadStores() {
  const [
    appOrdersStore,
    commercialisationStore,
    contractsStore,
    expeditionsStore,
    gisStore,
    gisEditorStore,
    presenceStore,
    productionStore,
    productionPlannerStore,
    repairsStore,
    traceabilityStore,
  ] = await Promise.all([
    import('../stores/appOrders'),
    import('../stores/commercialisation'),
    import('../stores/contracts'),
    import('../stores/expeditions'),
    import('../stores/gis'),
    import('../stores/gisEditor'),
    import('../stores/presence'),
    import('../stores/production'),
    import('../productionPlanner/stores/productionPlanner'),
    import('../stores/repairParts'),
    import('../stores/traceability'),
  ]);

  return {
    appOrdersStore: appOrdersStore.useAppOrdersStore(),
    commercialisationStore: commercialisationStore.useCommercialisationStore(),
    contractsStore: contractsStore.useContractsStore(),
    expeditionsStore: expeditionsStore.useExpeditionsStore(),
    gisStore: gisStore.useGisStore(),
    gisEditorStore: gisEditorStore.useGisEditorStore(),
    presenceStore: presenceStore.usePresenceStore(),
    productionStore: productionStore.useProductionStore(),
    productionPlanner: productionPlannerStore.useProductionPlannerStore(),
    repairsStore: repairsStore.useRepairPartsStore(),
    traceabilityStore: traceabilityStore.useTraceabilityStore(),
  };
}

export async function useResetStore() {
  const maybePinia = getActivePinia();

  if (!maybePinia) {
    throw new Error('There is no active Pinia instance');
  }

  const pinia = /** @type {ExtendedPinia} */ (maybePinia);

  const stores = await loadStores();

  /** @type {Record<string, () => void> & { all: () => void }} */
  const resetStore = {};

  pinia._s.forEach((store, name) => {
    resetStore[name] = () => store.$reset();
  });

  resetStore.all = () => Object.values(stores).forEach((store) => store.$reset());

  return resetStore;
}
